(ns hai.tasks.data
  "Shared task state, events, subscriptions, and domain helpers."
  (:require
   [clojure.string :as str]
   [hai.entity.draft :as draft]
   [hai.entity.lock :as lock]
   [hai.entity.staging :as staging]
   [hai.sharing.helper :as sharing]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.eql :as eql]
   [hai.tasks.urgency :as urgency]
   [hai.ui.request :as request]
   [hai.ui.rf-sugar :refer [>e! defevent defevent-fx defsub]]))

(defn- start-of-today []
  (doto (js/Date.) (.setHours 0 0 0 0)))

(defn task-bucket
  "Bucket a task by its `:task/urgency`. The three lanes map to their buckets;
   everything else — none, and a task with no urgency set at all — lands in
   :none."
  [task]
  (case (:task/urgency task)
    :task.urgency/now   :now
    :task.urgency/next  :next
    :task.urgency/later :later
    :none))

(defn staged-bucket
  "The bucket a task the composer just saved lands in. Its payload carries a
   bare `:urgency`, not the `:task/urgency` [[task-bucket]] reads."
  [{:keys [urgency]}]
  (task-bucket {:task/urgency urgency}))

(defn assignee-email
  "First email of a task's assignee; the attr may be a collection."
  [assignee]
  (let [email (:user/email assignee)]
    (if (coll? email) (first email) email)))

(defn assignee-name [assignee]
  (or (not-empty (:user/full-name assignee))
      (not-empty (assignee-email assignee))))

(defn contact-name
  "Display name for a contact picked in the assignee picker. That's the shape a
   staged, not-yet-saved assignee has — `task/assign` is what turns it into the
   user `assignee-name` reads."
  [contact]
  (or (not-empty (:contact/full-name contact))
      (not-empty (first (:contact/email contact)))))

(defn picked-assignee?
  "True when `assignee` is a fresh pick the picker staged and `task/assign` can
   apply — either a contact (`:contact/uuid`) or a raw email typed for someone
   not in the requester's contacts (`:user/email` with no `:user/uuid`). An
   untouched picker still holds the task's resolved assignee, which carries a
   `:user/uuid`; that's left alone."
  [assignee]
  (boolean (or (:contact/uuid assignee)
               (and (:user/email assignee) (not (:user/uuid assignee))))))

(defn assignee->assign-params
  "`task/assign` params for a staged pick — a contact by uuid, or a raw email.
   A typed email carries `:contact/add?` (the picker's \"Add contact\" checkbox):
   whether the backend should also save the address to the requester's personal
   contacts. Defaults to true when the pick predates the checkbox."
  [assignee]
  (if-let [contact-uuid (:contact/uuid assignee)]
    {:contact/uuid contact-uuid}
    {:user/email (assignee-email assignee)
     :contact/add? (get assignee :contact/add? true)}))

(defn assignee-me? [current-user assignee]
  (= (:user/uuid current-user)
     (:user/uuid assignee)))

(defn can-complete?
  "Whether `current-user` may stamp the task complete or lift the stamp: its
   owner or its assignee. An admin who is neither edits and shares, but does
   not stamp."
  [current-user task]
  (or (sharing/owner? task)
      (assignee-me? current-user (:task/assigned-to task))))

(defn can-assign?
  "Whether the requester may set or clear the task's assignee. Assigning
   grants the assignee admin, so it takes the sharing right: owner or admin.
   A contributor edits the rest of the task."
  [task]
  (sharing/can-share? task))

(defn assignee-initials [assignee]
  (let [from-name (->> (str/split (or (:user/full-name assignee) "") #"\s+")
                       (keep first)
                       (take 2)
                       (apply str))
        email (assignee-email assignee)]
    (cond
      (seq from-name) (str/upper-case from-name)
      (seq email)     (str/upper-case (subs email 0 1)))))

(def ^:private user-eql
  "The user attributes a tasks surface reads, fetched as part of the tasks loads,
   and alone only when another tab changed them. The web-app's `/me` already puts
   them on `:auth/identity`; this is how the extension's — minted from the JWT
   alone — gets them, and how a copy that went stale in another client is
   refreshed. Keep this list and [[user-config-loaded]]'s merge target the same
   `:auth/identity` keys, so adding an attribute is one line here."
  [:user-config.tasks/default-urgency])

;; Home's "My Tasks" mini-app loads the tasks the current user owns or is
;; assigned: every open one, plus any completed earlier today so the day's
;; finished items still show (struck through). `:user/current-tasks` returns
;; both in a single request, newest first.
(defevent-fx ::load-owned-or-assigned-tasks [_ & [extra-success-fx extra-error-fx]]
  {:resolve {:query (into [{(list :user/current-tasks {:completed-since (start-of-today)})
                            eql/task-eql}]
                          user-eql)
             :store-in [::load-result]
             :transform-fn (fn [resp] (get resp :user/current-tasks []))
             :on-success-fx (into [[:dispatch [::loaded]]
                                   [:dispatch [::user-config-loaded '?body]]]
                                  extra-success-fx)
             :on-error-fx (vec extra-error-fx)}})

;; The user attributes riding the response, onto the identity — the one place
;; every surface reads them from.
(defevent ::user-config-loaded [db response]
  (update db :auth/identity merge (select-keys response user-eql)))

(defevent-fx ::load-user-config [_ _]
  {:resolve {:query user-eql
             :on-success-fx [[:dispatch [::user-config-loaded '?body]]]}})

;; The full /app/tasks page loads every task the user can see — their own and
;; ones shared with them — but only the open ones; completed tasks live in the
;; page's separate Completed archive. Newest first.
(defevent-fx ::load-all-my-tasks [_ _]
  {:resolve {:query (into [{(list :user.tasks/table
                                  {:pagination {:per-page 500
                                                :attrs [:task/created-at]
                                                :direction :desc}
                                   :collection.filters/selected {:task/open? true}})
                            [{:collection/items eql/task-eql}]}]
                          user-eql)
             :store-in [::load-result]
             :transform-fn (fn [resp] (get-in resp [:user.tasks/table :collection/items] []))
             :on-success-fx [[:dispatch [::loaded]]
                             [:dispatch [::user-config-loaded '?body]]]}})

;; Both loads land in the same `::tasks` slice — the one both surfaces read.
(defevent ::loaded [db _]
  (-> db
      (assoc ::tasks (get db ::load-result []))
      (dissoc ::load-result)))

(defn- error-text
  "User-facing toast text for a failed save, from the `:on-error-fx` body."
  [response]
  (if (= :activity/url (-> response :com.wsscode.pathom3.error/error-data :attr))
    "Could not save task. Check that your links are valid URLs."
    "Could not save task. Try again."))

(defn picked->assignee
  "Optimistic `:task/assigned-to` shape from a staged pick — a contact or a raw
   email. The same `:user/full-name` + `:user/email` shape the row reads, so the
   just-created task shows the assignee right away, and the new-task form's
   bubble shows its pick. It has no `:user/uuid` until `task/assign` resolves
   and swaps in the real user."
  [picked]
  (if (:contact/uuid picked)
    {:user/full-name (:contact/full-name picked)
     :user/email (:contact/email picked)}
    {:user/email (:user/email picked)}))

(def board-collection-id
  "Collection id of the tasks-page :query kanban board ([HAI-001-04]). Owned here,
  in the data layer, so data-layer success handlers (e.g. `::create-resolved`)
  can reconcile the board without a circular dependency on the page namespace;
  `hai.tasks.page` reads it back for the board's `:id` and its per-task
  re-resolves."
  ::board)

(defn- staged-chips
  "The composer stages whole entities; a cached task only carries the chip shape
   `task-eql` asks for. Empty families are dropped, so this merges onto a
   task without blanking what it already has. A tag typed fresh has no uuid
   until the server makes one — its name carries the chip until the re-resolve."
  [{:keys [contacts notes tasks tags]}]
  (into {}
        (remove (comp empty? val))
        {:task/contacts (mapv staging/contact-chip contacts)
         :task/notes (mapv staging/note-chip notes)
         :task/tasks (mapv staging/task-chip tasks)
         :task/tags (mapv staging/tag-chip tags)}))

(def composer-draft-id
  "The draft behind the Add-a-task composer. One at a time, and the desktop and
   mobile home cards are the same one."
  ::composer-draft)

;; The mini-app cards open one task at a time over the whole card, in place of
;; the list. Back, or deleting the task, puts the list back.
(defevent ::open-editor [db task-uuid]
  (assoc db ::editor-task-uuid task-uuid))

(defevent ::close-editor [db _]
  (dissoc db ::editor-task-uuid))

(defsub ::editor-task-uuid [db]
  (get db ::editor-task-uuid))

;; The open task as the slice has it now; nil once it leaves the slice.
(defsub ::editor-task
  [task-uuid <- [::editor-task-uuid]
   tasks <- [::list]]
  (when task-uuid
    (some #(when (= task-uuid (:task/uuid %)) %) tasks)))

;; ---------------------------------------------------------------------------
;; Default urgency — the lane a new task starts in, picked from the ＋ pill in
;; the tasks header ([[hai.tasks.add-pill]]) and kept on the user, so it holds
;; across sessions and surfaces. It lives on `:auth/identity` and nowhere else:
;; `/me` puts it there on the web-app, the tasks load ([[user-eql]]) does on the
;; extension and refreshes it on both, and the pick below writes the same key
;; and has the other open tabs refetch it. With no pick made, a new task starts
;; with no urgency.
;; ---------------------------------------------------------------------------

(defn default-urgency
  "The urgency a new task starts at, for events that seed a composer."
  [db]
  (or (get-in db [:auth/identity :user-config.tasks/default-urgency])
      urgency/none))

(defsub ::default-urgency [db]
  (default-urgency db))

(defevent-fx ::set-default-urgency
  [{:keys [db]} value]
  {:db (assoc-in db [:auth/identity :user-config.tasks/default-urgency] value)
   :resolve {:query `[(~'user/update
                       ~{:user/uuid (get-in db [:auth/identity :user/uuid])
                         :user-config.tasks/default-urgency value})]
             :on-success-fx [[:dispatch [:sync/changed {:scope :user}]]]}})

;; The home card's Add-a-task composer, open until its create lands.
(defevent ::open-composer [db staged]
  (-> db
      (assoc ::composer-open? true)
      (draft/seed composer-draft-id staged)))

(defevent ::close-composer [db _]
  (-> db
      (dissoc ::composer-open?)
      (draft/discard composer-draft-id)))

(defsub ::composer-open? [db]
  (get db ::composer-open? false))

(defn- task-link-action
  "The success toast's \"View task\" link, from a host's `:task-link` —
   `{:href-fn (fn [uuid] url) :target \"_self\" :label \"View task\"}`. The host
   owns the URL shape — a client-side route in the web app, an absolute platform
   URL in the extension — since this shared code can't know either. nil without
   one, which leaves the toast plain text.

   Hosts that are already showing the task pass nothing: the task detail page's
   own save would be linking to the page you are reading it on."
  [{:keys [href-fn target label]} task-uuid]
  (when href-fn
    {:label  (or label "View task")
     :href   (href-fn task-uuid)
     :target target}))

;; Every task mutation carries this on success, so the other open tabs refetch;
;; each surface handles `:sync/changed`.
(def ^:private tasks-changed-fx
  [:dispatch [:sync/changed {:scope :tasks}]])

;; ---------------------------------------------------------------------------
;; How an open task's writes are going, for its save mark
;; ([[hai.tasks.sync-mark]]): typed and not sent yet, in flight, or just
;; landed. Every write to a task counts, not only what is typed.
;; ---------------------------------------------------------------------------

(defn- write-started
  [db task-uuid]
  (update-in db [::writes task-uuid :in-flight] (fnil inc 0)))

(defn- write-ended-fx
  "Closes a write [[write-started]] opened; `landed?` when it was saved."
  [task-uuid landed?]
  [:dispatch [::write-ended task-uuid landed?]])

(defn- typing
  "`db` with `field` of the task typed into and not sent yet, or not."
  [db task-uuid field typing?]
  (update-in db [::writes task-uuid :typing] (fnil (if typing? conj disj) #{}) field))

;; The last write in flight landing runs a re-read a Refresh left waiting
;; ([[::sync-now]]).
(defevent-fx ::write-ended
  [{:keys [db]} task-uuid landed?]
  (let [now (js/Date.)
        db (cond-> (update-in db [::writes task-uuid :in-flight] #(max 0 (dec (or % 1))))
             landed? (assoc-in [::writes task-uuid :landed-at] now))
        {:keys [in-flight refresh-fx]} (get-in db [::writes task-uuid])
        settled? (zero? in-flight)
        settled-refresh (when settled? refresh-fx)]
    (cond-> {:db (cond-> db
                   settled? (update-in [::writes task-uuid] dissoc :typed?)
                   settled-refresh (update-in [::writes task-uuid] dissoc :refresh-fx))
             :fx (vec settled-refresh)}
      landed? (assoc :dispatch-later {:ms 1000 :dispatch [::landing-shown task-uuid now]}))))

;; The tick shows for a second after the latest landing, not after each.
(defevent ::landing-shown [db task-uuid landed-at]
  (cond-> db
    (= landed-at (get-in db [::writes task-uuid :landed-at]))
    (update-in [::writes task-uuid] dissoc :landed-at)))

;; `:pending` while something typed waits for its pause, and on through the
;; write that typing started; `:syncing` while any other write is in flight;
;; `:done` for a second after one lands; `:idle` otherwise.
(defsub ::sync [db task-uuid]
  (let [{:keys [typing in-flight typed? landed-at]} (get-in db [::writes task-uuid])
        writing? (pos? (or in-flight 0))]
    (cond
      (seq typing) :pending
      (and writing? typed?) :pending
      writing? :syncing
      landed-at :done
      :else :idle)))

;; ---------------------------------------------------------------------------
;; An open task's history, for its save mark's popover: fetched when the
;; popover opens, and again by its Refresh ([[::refresh]]).
;; ---------------------------------------------------------------------------

(def ^:private history-eql
  [{:task/history [:task.change/at
                   :task.change/parts
                   {:task.change/by [:user/uuid :user/full-name :user/email]}
                   {:task.change/assigned-from [:user/uuid]}
                   {:task.change/assigned-to [:user/uuid :user/full-name :user/email]}]}])

(defevent-fx ::load-history
  [{:keys [db]} task-uuid]
  {:db (update-in db [::histories task-uuid] assoc :loading? true :failed? false)
   :resolve {:query [{[:task/uuid task-uuid] history-eql}]
             :on-success-fx [[:dispatch [::history-loaded task-uuid '?body]]]
             :on-error-fx [[:dispatch [::history-failed task-uuid]]]}})

(defevent ::history-loaded [db task-uuid response]
  (assoc-in db [::histories task-uuid]
            {:changes (get-in response [[:task/uuid task-uuid] :task/history])}))

;; What it showed before stays: a failed Refresh takes nothing away.
(defevent ::history-failed [db task-uuid]
  (update-in db [::histories task-uuid]
             assoc :loading? false :failed? true :refreshing? false))

;; `{:changes … :loading? … :failed? … :refreshing? …}`, the changes newest
;; first; `:refreshing?` from a press on Refresh until its history is back.
(defsub ::history [db task-uuid]
  (get-in db [::histories task-uuid]))

(defn task-ident
  "Entity-store key for a task, matching `hai.entity.helpers/->ident` so the
  detail page reads the same copy through the shared `:entity` subscription.
  Also the ident its field locks go by ([[hai.entity.lock]])."
  [task-uuid]
  [:task/uuid (uuid (str task-uuid))])

(defn- patch-task
  "Update one task by uuid in the `::tasks` slice and, when present, the shared
  entity store, so optimistic edits show on the list and the detail page alike."
  [db task-uuid f]
  (let [patch-in (fn [tasks]
                   (mapv (fn [task]
                           (if (= task-uuid (:task/uuid task)) (f task) task))
                         tasks))]
    (cond-> (update db ::tasks patch-in)
      (get-in db (task-ident task-uuid)) (update-in (task-ident task-uuid) f))))

;; `:request-id` names the request, for the composer's in-flight state;
;; `:success-fx` rides the create's response — the host closing its composer.
;; `:task-link`, when supplied, links the success toast to the new task
;; ([[task-link-action]]).
(defevent-fx ::create
  [{:keys [db]} {:keys [title description urgency deadline urls assignee
                        contacts notes tasks tags shares request-id success-fx
                        task-link] :as staged}]
  (let [new-uuid (random-uuid)
        creator (select-keys (:auth/identity db) [:user/uuid :user/full-name :user/email])
        now (js/Date.)
        deadline-params (deadline/value->params deadline)
        link-urls (vec (distinct urls))
        ;; A fresh pick (a contact, or a raw email) reassigns away from the
        ;; creator; an untouched picker still holding the creator leaves the
        ;; default assign-to-me alone.
        assignee-picked (when (picked-assignee? assignee) assignee)
        contact-uuids (mapv :contact/uuid contacts)
        note-uuids (mapv :note/uuid notes)
        task-uuids (mapv :task/uuid tasks)
        tag-uuids (vec (keep :group/uuid tags))
        tag-names (vec (keep :tag/name tags))
        task (merge (cond-> {:task/uuid new-uuid
                             :task/title title
                             :task/created-at now
                             :task/updated-at now
                             :task/assigned-to (if assignee-picked
                                                 (picked->assignee assignee-picked)
                                                 creator)
                             :entity/owner creator
                             :user.entity/owner? true
                             :user.entity/permissions [:permission.type/admin]}
                      (seq description) (assoc :task/description description)
                      urgency (assoc :task/urgency urgency)
                      deadline-params (merge deadline-params)
                      (seq link-urls) (assoc :task/activities
                                             (mapv (fn [url] {:activity/url url}) link-urls)))
                    ;; Embed the staged links and tags so the just-created card
                    ;; shows their chips instantly, before any re-resolve
                    ;; ([HAI-001-10]).
                    (staged-chips staged))
        params (cond-> {:task/title title
                        :task/uuid new-uuid}
                 (seq description) (assoc :task/description description)
                 urgency (assoc :task/urgency urgency)
                 (seq link-urls) (assoc :activity/urls link-urls)
                 deadline-params (merge deadline-params)
                 ;; Linked entities picked in the create composer; the backend
                 ;; `task/create` accepts these additively, same as `task/update`.
                 (seq contact-uuids) (assoc :task/contact-uuids contact-uuids)
                 (seq note-uuids) (assoc :task/note-uuids note-uuids)
                 (seq task-uuids) (assoc :task/task-uuids task-uuids)
                 (seq tag-uuids) (assoc :task/tag-uuids tag-uuids)
                 (seq tag-names) (assoc :task/tag-names tag-names))]
    (when-not (and request-id (request/in-flight? db request-id))
      {:db (cond-> (-> db
                       (update ::tasks #(into [task] %))
                       (assoc ::highlighted-uuid new-uuid))
             request-id (request/mark-in-flight request-id))
       ;; Optimistically prepend to the :query board too ([HAI-001-10]): it renders
       ;; from `:collections`, not the `::tasks` slice above, so without this the
       ;; new card wouldn't show until the create re-resolves. Instant feedback,
       ;; consistent with the other per-task board actions ([HAI-001-07]); the
       ;; success re-resolve reconciles, and `::create-failed` rolls it back.
       ;; Mount-guarded inside `:table/add-row` (creation also fires off-board).
       :fx [[:dispatch [:table/add-row board-collection-id task]]]
       ;; Briefly flag the just-added row so the card can flash it, then let it
       ;; settle. Keyed by uuid so only this task lights up, not every row on load.
       :dispatch-later {:ms 2000 :dispatch [::clear-highlight]}
       ;; The answer is the task as a read of it gives it, to replace the
       ;; optimistic copy with ([[::create-resolved]]).
       :resolve {:query [{(list 'task/create params) eql/task-eql}]
                 :request-id request-id
                 :on-success-fx (into [[:dispatch [::create-resolved new-uuid assignee-picked
                                                   (task-link-action task-link new-uuid)
                                                   (mapv #(select-keys % [:share :permission]) shares)
                                                   '?body]]
                                       tasks-changed-fx]
                                      success-fx)
                 :on-error-fx [[:dispatch [::create-failed new-uuid '?body]]]}})))

(defevent ::clear-highlight [db _]
  (dissoc db ::highlighted-uuid))

(defevent-fx ::create-failed [{:keys [db]} new-uuid response]
  {:db (update db ::tasks
               (fn [tasks]
                 (filterv #(not= new-uuid (:task/uuid %)) tasks)))
   ;; Roll the optimistic board row back too ([HAI-001-10]), mount-guarded like
   ;; the add.
   :fx [[:dispatch [:table/remove-row board-collection-id :task/uuid new-uuid]]
        [:dispatch [:toast/error {:text (error-text response)}]]]})

(defevent-fx ::create-resolved
  [{:keys [db]} new-uuid assignee-picked toast-action grants response]
  ;; The task now exists, so a picked assignee can be applied — `task/assign`
  ;; reassigns it off the creator and grants the assignee access. Its own
  ;; resolver swaps the optimistic contact for the real user. The staged
  ;; shares go first: a share sets its person's one role, so the assignee's
  ;; admin has to land after it.
  (let [created (get response 'task/create)
        ;; `::create` optimistically prepended the new card to the :query board
        ;; ([HAI-001-10]); this re-resolve is the reconciling authority that
        ;; replaces the optimistic row with server truth (the [HAI-001-07]
        ;; patch-then-reconcile pattern). Mount-guarded: creation also fires
        ;; from the mobile add-sheet / universal bar, where the board isn't
        ;; rendered.
        reconcile-board [:dispatch [:table/resolve-if-mounted board-collection-id]]
        ;; A picked assignee resolves *after* this via `task/assign`; carry
        ;; the reconcile through it instead, so the board holds the
        ;; optimistic (picked-assignee) row until the final user lands -
        ;; reconciling now would flash the pre-assign creator over it.
        assign-fx (when assignee-picked
                    [:dispatch [::assign new-uuid assignee-picked [reconcile-board]]])]
    {;; The platform's task in place of the optimistic copy, so what only it
     ;; knows — the owner flag, a new tag's uuid — is there for the next click.
     ;; A picked assignee goes after the create, so until it lands the copy's
     ;; pick stands over the creator the platform assigned.
     :db (cond-> db
           (:task/uuid created)
           (patch-task new-uuid (fn [optimistic]
                                  (cond-> created
                                    assignee-picked (assoc :task/assigned-to (:task/assigned-to optimistic))))))
     :fx (cond-> [[:dispatch [:toast/success (cond-> {:text "Task added"}
                                               toast-action (assoc :action toast-action))]]
                  ;; A new task shifts the board's total + its assignee's count,
                  ;; so refresh the facet sidebar (mount-guarded in ::load-facets).
                  [:dispatch [::load-facets]]]
           ;; No reassignment: reconcile now (the optimistic row already
           ;; matches server truth bar server-normalised fields).
           (not assignee-picked) (conj reconcile-board)
           (seq grants) (conj [:dispatch [::share new-uuid grants (if assign-fx [assign-fx] [])]])
           ;; With no shares to wait for, the assignee goes at once.
           (and assign-fx (empty? grants)) (conj assign-fx))}))

;; Completion is optimistic: the patch alone moves the task between Home's open
;; and struck states (and off the page's open buckets), so no reload is needed.
;; A caller with its own dependent state — e.g. the tasks page's Completed
;; archive — passes it as `extra-success-fx`.
(defevent-fx ::complete [{:keys [db]} task-uuid & [extra-success-fx]]
  {:db (-> db
           (patch-task task-uuid #(assoc % :task/completed-at (js/Date.)))
           (write-started task-uuid))
   :resolve {:query [{(list 'task/complete {:task/uuid task-uuid})
                      [:task/uuid :task/completed-at]}]
             ;; Completing drops the task off the open board, shifting the facet
             ;; total + its assignee's count; refresh the sidebar.
             :on-success-fx (conj (vec extra-success-fx)
                                  [:dispatch [::load-facets]]
                                  tasks-changed-fx
                                  (write-ended-fx task-uuid true))
             :on-error-fx [(write-ended-fx task-uuid false)]}})

(defevent-fx ::uncomplete [{:keys [db]} task-uuid & [extra-success-fx]]
  {:db (-> db
           (patch-task task-uuid #(dissoc % :task/completed-at))
           (write-started task-uuid))
   :resolve {:query [{(list 'task/uncomplete {:task/uuid task-uuid}) [:task/uuid]}]
             ;; Un-completing returns the task to the open board, shifting the
             ;; facet total + its assignee's count; refresh the sidebar.
             :on-success-fx (conj (vec extra-success-fx)
                                  [:dispatch [::load-facets]]
                                  tasks-changed-fx
                                  (write-ended-fx task-uuid true))
             :on-error-fx [(write-ended-fx task-uuid false)]}})

(defevent-fx ::delete [{:keys [db]} task-uuid & [extra-success-fx]]
  (let [deleted (some #(when (= task-uuid (:task/uuid %)) %) (get db ::tasks []))]
    {:db (update db ::tasks (fn [tasks] (filterv #(not= task-uuid (:task/uuid %)) tasks)))
     ;; No join: `task/delete` returns `{}`, so asking it for `:task/uuid` back
     ;; leaves Pathom no path to it and reports the whole delete as failed.
     :resolve {:query [(list 'task/delete {:task/uuid task-uuid})]
               :on-success-fx (conj (vec extra-success-fx)
                                    [:dispatch [:toast/success {:text "Task deleted"}]]
                                    ;; Removing a task shifts the board total + the
                                    ;; assignee's count; refresh the facet sidebar.
                                    [:dispatch [::load-facets]]
                                    tasks-changed-fx)
               :on-error-fx [[:dispatch [::delete-failed deleted]]]}}))

;; Both loads sort newest first, so the restored task goes back by created-at
;; rather than to the head of the list it was cut from.
(defevent-fx ::delete-failed [{:keys [db]} task]
  {:db (cond-> db
         task (update ::tasks
                      (fn [tasks]
                        (vec (sort-by :task/created-at
                                      #(compare %2 %1)
                                      (conj (vec tasks) task))))))
   :fx [[:dispatch [:table/resolve-if-mounted board-collection-id]]
        [:dispatch [:toast/error {:text "Could not delete task. Try again."}]]]})

(defevent-fx ::update
  [{:keys [db]} {:keys [task-uuid urgency deadline clear-deadline?
                        clear-assignee? urls
                        tag-uuids tag-names untag-uuids contact-uuids note-uuids task-uuids
                        request-id]}
   & [extra-success-fx]]
  (let [prev-task (some #(when (= task-uuid (:task/uuid %)) %)
                        (get db ::tasks []))
        deadline-params (deadline/value->params deadline)
        link-urls (vec (distinct urls))
        patch (cond-> {}
                clear-assignee? (assoc :task/assigned-to nil)
                (some? urgency) (assoc :task/urgency urgency)
                deadline-params (merge deadline-params)
                clear-deadline? (assoc :task/deadline-at nil
                                       :task/deadline-all-day? nil
                                       :task/deadline-timezone nil)
                (some? urls) (assoc :task/activities
                                    (mapv (fn [url] {:activity/url url}) link-urls)))
        rollback (into {} (map (fn [k] [k (get prev-task k)])) (keys patch))
        ;; Tags and entity links carry no optimistic patch — they are not on the
        ;; cached card — so they ride the params only, and the re-resolve is what
        ;; shows them.
        params (cond-> (merge {:task/uuid task-uuid}
                              (dissoc patch :task/activities :task/assigned-to))
                 clear-deadline? (assoc :clear-deadline? true)
                 clear-assignee? (assoc :clear-assignee? true)
                 (some? urls) (assoc :activity/urls link-urls)
                 (seq tag-uuids) (assoc :task/tag-uuids (vec tag-uuids))
                 (seq tag-names) (assoc :task/tag-names (vec tag-names))
                 (seq untag-uuids) (assoc :task/untag-uuids (vec untag-uuids))
                 (seq contact-uuids) (assoc :task/contact-uuids (vec contact-uuids))
                 (seq note-uuids) (assoc :task/note-uuids (vec note-uuids))
                 (seq task-uuids) (assoc :task/task-uuids (vec task-uuids)))]
    {:db (-> db
             (patch-task task-uuid #(merge % patch))
             (write-started task-uuid))
     ;; The board renders from `:collections`, not the slice patched above.
     :fx (cond-> []
           (seq patch) (conj [:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid patch]]))
     :resolve {:query [{(list 'task/update params) [:task/uuid]}]
               :request-id request-id
               :on-success-fx (conj (vec extra-success-fx)
                                    tasks-changed-fx
                                    (write-ended-fx task-uuid true))
               :on-error-fx [[:dispatch [::update-failed task-uuid rollback '?body]]]}}))

(defn- unlinked-uuids
  "The uuids the composer dropped: what the task has linked, minus what came
   back staged."
  [id-key linked staged]
  (let [staged-uuids (set (keep id-key staged))]
    (vec (remove staged-uuids (keep id-key linked)))))

(defn- added-uuids
  "The uuids the composer newly staged: what came back staged, minus what the
   task already has. Tags need this diff — `task/update` asserts the requester
   owns every tag uuid it is sent, so re-sending one that is merely riding along
   rejects the whole save on a task shared by someone else."
  [id-key linked staged]
  (let [linked-uuids (set (keep id-key linked))]
    (vec (remove linked-uuids (keep id-key staged)))))

(defn- update-params
  "`::update`'s payload for the fields in `changes` an existing task carries
   through `task/update`, or nil when the click changed none of them. Links and
   tags go as deltas: `task/update` asserts the requester owns every tag and
   contact it is sent and can read every note and task, and a task shared with
   you can carry links to things you cannot open — re-sending one that is
   merely riding along rejects the save."
  [task {:keys [urgency deadline urls contacts notes tasks] :as changes} tags]
  (let [changed? #(contains? changes %)
        params (cond-> {}
                 (changed? :urgency) (assoc :urgency urgency)
                 (changed? :deadline) (assoc :deadline deadline
                                             :clear-deadline? (boolean (and (:task/deadline-at task)
                                                                            (nil? (:date deadline)))))
                 (changed? :urls) (assoc :urls urls)
                 (changed? :contacts) (assoc :contact-uuids (added-uuids :contact/uuid (:task/contacts task) contacts))
                 (changed? :notes) (assoc :note-uuids (added-uuids :note/uuid (:task/notes task) notes))
                 (changed? :tasks) (assoc :task-uuids (added-uuids :task/uuid (:task/tasks task) tasks))
                 (changed? :tags) (assoc :tag-uuids (added-uuids :group/uuid (:task/tags task) tags)
                                         :tag-names (vec (keep :tag/name tags))
                                         :untag-uuids (unlinked-uuids :group/uuid (:task/tags task) tags)))
        deltas [:contact-uuids :note-uuids :task-uuids :tag-uuids :tag-names :untag-uuids]]
    (when (or (some changed? [:urgency :deadline :urls])
              (some #(seq (get params %)) deltas))
      (assoc params :task-uuid (:task/uuid task)))))

;; One click's change to an existing task. `changes` holds the fields it touched,
;; staged as the form stages them ([[hai.tasks.form/from-task]]). Scalars, the
;; url set, tags and newly linked entities ride one `task/update`; a dropped
;; link its own `task/remove-*`, and spaces their own mutations. The typed
;; fields save through [[::type-text]] instead. `change-fx` rides every
;; response: the host's refresh.
(defevent-fx ::save-fields
  [{:keys [db]} task changes change-fx]
  (let [task-uuid (:task/uuid task)
        {:keys [contacts notes tasks spaces]} changes
        changed? #(contains? changes %)
        tags (when (changed? :tags)
               (staging/reconcile-tags (:task/tags task) (:tags changes)))
        update-payload (update-params task changes tags)
        ;; Links and tags carry no patch of their own through `::update` — they
        ;; are not scalars on the card — so the staged chips land here.
        chips (cond-> {}
                (changed? :contacts) (assoc :task/contacts (mapv staging/contact-chip contacts))
                (changed? :notes) (assoc :task/notes (mapv staging/note-chip notes))
                (changed? :tasks) (assoc :task/tasks (mapv staging/task-chip tasks))
                (changed? :tags) (assoc :task/tags (mapv staging/tag-chip tags)))
        dropped (fn [id-key linked staged]
                  (when (changed? staged)
                    (unlinked-uuids id-key (get task linked) (get changes staged))))]
    {:db (patch-task db task-uuid #(merge % chips))
     :fx (-> (cond-> []
               update-payload (conj [:dispatch [::update update-payload change-fx]]))
             (into (for [contact-uuid (dropped :contact/uuid :task/contacts :contacts)]
                     [:dispatch [::remove-contact {:task/uuid task-uuid
                                                   :contact/uuid contact-uuid}
                                 change-fx]]))
             (into (for [note-uuid (dropped :note/uuid :task/notes :notes)]
                     [:dispatch [::remove-note-link {:task/uuid task-uuid
                                                     :note/uuid note-uuid}
                                 change-fx]]))
             (into (for [linked-uuid (dropped :task/uuid :task/tasks :tasks)]
                     [:dispatch [::remove-task-link {:task/uuid task-uuid
                                                     :task/linked-uuid linked-uuid}
                                 change-fx]]))
             (into (when (changed? :spaces)
                     (for [space-uuid (added-uuids :space/uuid (:entity/spaces task) spaces)]
                       [:dispatch [::add-to-space task-uuid space-uuid change-fx]])))
             (into (for [space-uuid (dropped :space/uuid :entity/spaces :spaces)]
                     [:dispatch [::remove-from-space task-uuid space-uuid change-fx]])))}))

(defsub ::contacts [db]
  (get db ::contacts))

;; The search text that produced whatever is currently in `::contacts`. The
;; picker compares it against the live input to tell "search still in flight"
;; (query ≠ input) from "search done, nothing matched" (query = input), so it
;; doesn't flash "No matching contacts" between a keystroke and its response.
(defsub ::contacts-query [db]
  (get db ::contacts-query))

;; Server-side contact search backing the assignee picker. `:contact/search`
;; filters in Datomic by name/email and returns nothing for a blank query, so
;; the picker searches as-you-type rather than fetching the whole list once.
;; Contacts without an email are dropped here — the `task/assign` mutation
;; resolves the assignee by email, so an email-less contact can't be assigned.
(defevent-fx ::search-contacts [_ text]
  {:resolve {:query [{(list :contact/search {:contact/search-text text})
                      [:contact/uuid :contact/full-name :contact/email]}]
             :store-in [::contacts-load-result]
             :transform-fn (fn [resp]
                             (->> resp
                                  :contact/search
                                  (filter #(seq (:contact/email %)))))
             :on-success-fx [[:dispatch [::loaded-contacts text]]]}})

;; Commit results + the query that produced them together, so a consumer never
;; sees the new list paired with a stale query (or vice versa).
(defevent ::loaded-contacts [db text]
  (-> db
      (assoc ::contacts (get db ::contacts-load-result []))
      (assoc ::contacts-query text)
      (dissoc ::contacts-load-result)))

(defevent ::clear-contacts [db _]
  (dissoc db ::contacts ::contacts-query ::contacts-load-result))

(defevent-fx ::assign [{:keys [db]} task-uuid assignee & [extra-success-fx]]
  ;; Optimistic: the picked contact/email shows as the assignee right away —
  ;; the same shape ::create embeds — on the slice, the entity store, and the
  ;; :query board. `task/assign`'s response swaps in the real user, and an
  ;; error restores the previous assignee.
  (let [optimistic (picked->assignee assignee)
        previous (:task/assigned-to (or (some #(when (= task-uuid (:task/uuid %)) %)
                                              (get db ::tasks []))
                                        (get-in db (task-ident task-uuid))))]
    {:db (-> db
             (patch-task task-uuid #(assoc % :task/assigned-to optimistic))
             (write-started task-uuid))
     :fx [[:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid
                      {:task/assigned-to optimistic}]]]
     :resolve {:query [{(list 'task/assign (merge {:task/uuid task-uuid}
                                                  (assignee->assign-params assignee)))
                        [:task/uuid
                         {:task/assigned-to [:user/uuid :user/full-name :user/email]}]}
                       ;; Assigning grants access, so the shares are read back
                       ;; after it.
                       {(task-ident task-uuid) [eql/shared-with-eql]}]
               ;; Reassigning moves a task between assignee options, so refresh
               ;; the facet sidebar's counts (mount-guarded in ::load-facets).
               :on-success-fx (into [[:dispatch [::assign-resolved task-uuid '?body]]
                                     [:dispatch [::load-facets]]
                                     tasks-changed-fx
                                     (write-ended-fx task-uuid true)]
                                    extra-success-fx)
               :on-error-fx [[:dispatch [::assign-failed task-uuid previous]]]}}))

(defevent-fx ::assign-failed [{:keys [db]} task-uuid previous]
  {:db (patch-task db task-uuid #(assoc % :task/assigned-to previous))
   :fx [[:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid
                    {:task/assigned-to previous}]]
        [:dispatch [:toast/error {:text "Could not assign task."}]]
        (write-ended-fx task-uuid false)]})

(defevent ::assign-resolved
  [db task-uuid response]
  (patch-task db task-uuid
              #(merge %
                      (select-keys (get response 'task/assign)
                                   [:task/assigned-to])
                      (select-keys (get response (task-ident task-uuid))
                                   [:entity/shared-with]))))

(defevent-fx ::update-failed [{:keys [db]} task-uuid rollback response]
  {:db (patch-task db task-uuid
                   (fn [task]
                     (reduce-kv (fn [t k v]
                                  (if (nil? v) (dissoc t k) (assoc t k v)))
                                task rollback)))
   :fx (cond-> [[:dispatch [:toast/error {:text (error-text response)}]]
                (write-ended-fx task-uuid false)]
         (seq rollback) (conj [:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid rollback]]))})

;; ---------------------------------------------------------------------------
;; Dropped linked entities (contacts / notes / tasks). `::save-fields`
;; dispatches these for the chips a click dropped; its patch already hides
;; them, so each just detaches through its `task/remove-*` mutation and
;; re-resolves the detail page's entity-store copy to server truth. Each takes
;; the host's `extra-success-fx`, as `::update` does.
;; ---------------------------------------------------------------------------

(defn- links-refresh-fx
  "Re-resolve the detail page's entity-store copy of the task after a link
   mutation, so its `:task/contacts` / `:task/notes` / `:task/tasks` catch up.
   Guarded on the task being in the store — links only surface on the detail
   page, whose route controller seeded it there."
  [db task-uuid]
  (cond-> []
    (get-in db (task-ident task-uuid))
    (conj [:dispatch [:entity/resolve {:task/uuid task-uuid}]])))

(defn- link-changed-fx
  [db task-uuid extra-success-fx]
  (-> (links-refresh-fx db task-uuid)
      (conj tasks-changed-fx (write-ended-fx task-uuid true))
      (into extra-success-fx)))

(defn- link-failed-fx
  [task-uuid text]
  [[:dispatch [:toast/error {:text text}]]
   (write-ended-fx task-uuid false)])

;; Spaces reuse the generic space mutations against the task, then re-resolve it
;; so its `:entity/spaces` chips catch up. `::save-fields` dispatches these for
;; the spaces a click added or dropped.
(defevent-fx ::add-to-space [{:keys [db]} task-uuid space-uuid & [extra-success-fx]]
  {:db (write-started db task-uuid)
   :resolve {:query [(list 'space/add-item {:space/uuid space-uuid :task/uuid task-uuid})]
             :on-success-fx (link-changed-fx db task-uuid extra-success-fx)
             :on-error-fx (link-failed-fx task-uuid "Could not add to space. Try again.")}})

(defevent-fx ::remove-from-space [{:keys [db]} task-uuid space-uuid & [extra-success-fx]]
  {:db (write-started db task-uuid)
   :resolve {:query [(list 'space/remove-item {:space/uuid space-uuid :task/uuid task-uuid})]
             :on-success-fx (link-changed-fx db task-uuid extra-success-fx)
             :on-error-fx (link-failed-fx task-uuid "Could not remove from space. Try again.")}})

(defevent-fx ::remove-contact
  [{:keys [db]} {task-uuid :task/uuid contact-uuid :contact/uuid} & [extra-success-fx]]
  {:db (write-started db task-uuid)
   :resolve {:query [{(list 'task/remove-contact {:task/uuid task-uuid
                                                  :contact/uuid contact-uuid})
                      [:task/uuid {:task/contacts [:contact/uuid :contact/full-name :contact/organisation?]}]}]
             :on-success-fx (link-changed-fx db task-uuid extra-success-fx)
             :on-error-fx (link-failed-fx task-uuid "Could not remove contact. Try again.")}})

(defevent-fx ::remove-note-link
  [{:keys [db]} {task-uuid :task/uuid note-uuid :note/uuid} & [extra-success-fx]]
  {:db (write-started db task-uuid)
   :resolve {:query [{(list 'task/remove-note {:task/uuid task-uuid
                                               :note/uuid note-uuid})
                      [:task/uuid {:task/notes [:note/uuid :note/text]}]}]
             :on-success-fx (link-changed-fx db task-uuid extra-success-fx)
             :on-error-fx (link-failed-fx task-uuid "Could not remove linked note. Try again.")}})

;; The linked task's uuid rides as `:task/linked-uuid` (both entities are tasks;
;; the backend's `task/remove-task` disambiguates the same way).
(defevent-fx ::remove-task-link
  [{:keys [db]} {task-uuid :task/uuid linked-uuid :task/linked-uuid} & [extra-success-fx]]
  {:db (write-started db task-uuid)
   :resolve {:query [{(list 'task/remove-task {:task/uuid task-uuid
                                               :task/linked-uuid linked-uuid})
                      [:task/uuid {:task/tasks [:task/uuid :task/title]}]}]
             :on-success-fx (link-changed-fx db task-uuid extra-success-fx)
             :on-error-fx (link-failed-fx task-uuid "Could not remove linked task. Try again.")}})

;; ---------------------------------------------------------------------------
;; Who a task is shared with. Each change reads the shares back in its own
;; request, after its mutations.
;; ---------------------------------------------------------------------------

(defn- shares-query
  [task-uuid mutations]
  (conj (vec mutations) {(task-ident task-uuid) [eql/shared-with-eql]}))

(defn- shares-changed-fx
  [task-uuid extra-success-fx]
  (into [[:dispatch [::shares-resolved task-uuid '?body]]
         tasks-changed-fx
         (write-ended-fx task-uuid true)]
        extra-success-fx))

(defn- same-share?
  [share other]
  (= (sharing/->share-ref share) (sharing/->share-ref other)))

(defn- with-permission
  [shared-with share permission]
  (mapv (fn [entry]
          (cond-> entry
            (same-share? share entry) (assoc :entity/permissions [permission])))
        shared-with))

(defn- without-share
  [shared-with share]
  (vec (remove #(same-share? share %) shared-with)))

(defn- change-share
  "The fx changing one of the task's shares: `change` on the cached shares at
   once, `mutation` behind it, and the shares as they were back on a failure
   with `failure-text`."
  [db task-uuid {:keys [change mutation failure-text extra-success-fx]}]
  (let [previous (:entity/shared-with (some #(when (= task-uuid (:task/uuid %)) %)
                                            (get db ::tasks [])))]
    {:db (-> db
             (patch-task task-uuid #(update % :entity/shared-with change))
             (write-started task-uuid))
     :resolve {:query (shares-query task-uuid [mutation])
               :on-success-fx (shares-changed-fx task-uuid extra-success-fx)
               :on-error-fx [[:dispatch [::shares-failed task-uuid previous failure-text]]]}}))

(defevent ::shares-resolved
  [db task-uuid response]
  (patch-task db task-uuid
              #(merge % (select-keys (get response (task-ident task-uuid))
                                     [:entity/shared-with]))))

(defevent-fx ::shares-failed
  [{:keys [db]} task-uuid previous text]
  {:db (patch-task db task-uuid #(assoc % :entity/shared-with previous))
   :fx (link-failed-fx task-uuid text)})

;; Each of `grants` a `:share` as `share/create` takes it — a contact, an
;; address or a domain — at its `:permission`. `settled-fx` rides the
;; response whether the shares land or not.
(defevent-fx ::share
  [{:keys [db]} task-uuid grants & [settled-fx]]
  {:db (write-started db task-uuid)
   :resolve {:query (shares-query task-uuid
                                  (for [{:keys [share permission]} grants]
                                    (list 'share/create {:entity {:task/uuid task-uuid}
                                                         :share share
                                                         :permission permission})))
             :on-success-fx (shares-changed-fx task-uuid settled-fx)
             :on-error-fx (into (link-failed-fx task-uuid "Could not share task. Try again.")
                                settled-fx)}})

;; `share` is the shared-with entry whose role changes.
(defevent-fx ::set-share-permission
  [{:keys [db]} task-uuid share permission & [extra-success-fx]]
  (change-share db task-uuid
                {:change #(with-permission % share permission)
                 :mutation (list 'share/update-to {:entity {:task/uuid task-uuid}
                                                   :share (sharing/->share-ref share)
                                                   :permission permission})
                 :failure-text "Could not change the role. Try again."
                 :extra-success-fx extra-success-fx}))

(defevent-fx ::unshare
  [{:keys [db]} task-uuid share & [extra-success-fx]]
  (change-share db task-uuid
                {:change #(without-share % share)
                 :mutation (list 'share/delete {:entity {:task/uuid task-uuid}
                                                :share (sharing/->share-ref share)})
                 :failure-text "Could not remove access. Try again."
                 :extra-success-fx extra-success-fx}))

;; ---------------------------------------------------------------------------
;; Typing into an existing task. Its title and description are edited in one
;; place at a time ([[hai.entity.lock]]): the field's editor opens once its lock
;; is taken, what is typed saves after a pause, and closing the editor sends
;; what is left and releases the lock in one request. A field sends one request
;; at a time — a newer save or the close waits for the one in flight — so older
;; text never lands after newer. What is typed stays in the field's draft until
;; its close lands.
;; ---------------------------------------------------------------------------

(defn text-draft-id
  "The draft behind a typed field of an existing task, `field` being its
   attribute: `:task/title` or `:task/description`."
  [task-uuid field]
  [::text task-uuid field])

(defn- text-to-send
  "What is typed into `field` and not sent yet; nil when nothing is. A title
   goes trimmed, and never blank."
  [db task-uuid field]
  (let [typed (draft/current db (text-draft-id task-uuid field))
        value (cond-> typed
                (and typed (= :task/title field)) str/trim)]
    (when (and (some? value)
               (not= value (get-in db [::texts [task-uuid field] :sent]))
               (not (and (= :task/title field) (str/blank? value))))
      value)))

(defn- forget-text
  [db task-uuid field]
  (-> db
      (update ::texts dissoc [task-uuid field])
      (draft/discard (text-draft-id task-uuid field))
      (typing task-uuid field false)))

;; The field counts as being opened until its lock arrives or its form goes
;; away, whichever is first.
(defevent-fx ::open-text
  [{:keys [db]} task-uuid field & [{:keys [steal?]}]]
  {:db (update db ::opening (fnil conj #{}) [task-uuid field])
   :dispatch [::lock/take (task-ident task-uuid) field
              {:steal? steal?
               :on-taken-fx [[:dispatch [::text-opened task-uuid field '?body]]]}]})

;; The take answers with the field as saved, which this page's copy may be
;; behind: someone else's edit reaches it only when the task is read again.
;; The editor opens on the saved text — unless text typed here is still on its
;; way, which is newer. A lock that arrives after the form went away, with the
;; field no longer being opened, has nobody to edit under it and is let go.
(defevent-fx ::text-opened
  [{:keys [db]} task-uuid field body]
  (let [text-key [task-uuid field]
        given-up? (not (or (contains? (::opening db) text-key)
                           (lock/held? db (task-ident task-uuid) field)))
        db (update db ::opening disj text-key)
        saved-text (or (lock/->field-value body) "")]
    (cond
      given-up?
      {:db db
       :dispatch [::close-text task-uuid field nil]}

      (nil? (text-to-send db task-uuid field))
      {:db (-> db
               (patch-task task-uuid #(assoc % field saved-text))
               (draft/seed (text-draft-id task-uuid field) saved-text)
               (assoc-in [::texts text-key :sent] saved-text))}

      :else
      {:db db})))

;; `change-fx` is the host's refresh, for when the task has to be read again.
(defevent-fx ::type-text
  [{:keys [db]} task-uuid field value change-fx]
  {:db (-> db
           (draft/seed (text-draft-id task-uuid field) value)
           (assoc-in [::texts [task-uuid field] :change-fx] change-fx)
           (typing task-uuid field true))
   :debounce/dispatch {:id (text-draft-id task-uuid field)
                       :event [::send-text task-uuid field]
                       :duration 1000}})

;; Refresh, in the save mark's popover: the task as [[::sync-now]] fetches it,
;; and its history after it.
(defevent-fx ::refresh
  [{:keys [db]} task-uuid refresh-fx]
  {:db (assoc-in db [::histories task-uuid :refreshing?] true)
   :fx [[:dispatch [::sync-now task-uuid (conj (vec refresh-fx)
                                               [:dispatch [::load-history task-uuid]])]]]})

;; What is typed goes now rather than after its pause, and then `refresh-fx`,
;; the host's re-read, fetches the task as it is — once those writes have
;; landed, so it comes back with them.
(defevent-fx ::sync-now
  [{:keys [db]} task-uuid refresh-fx]
  {:fx (conj (into []
                   (mapcat (fn [field]
                             [[:debounce/stop {:id (text-draft-id task-uuid field)}]
                              [:dispatch [::send-text task-uuid field]]]))
                   (get-in db [::writes task-uuid :typing]))
             [:dispatch [::refresh-when-settled task-uuid refresh-fx]])})

;; Queued behind the sends above, so their writes are counted by now.
(defevent-fx ::refresh-when-settled
  [{:keys [db]} task-uuid refresh-fx]
  (if (pos? (get-in db [::writes task-uuid :in-flight] 0))
    {:db (assoc-in db [::writes task-uuid :refresh-fx] refresh-fx)}
    {:fx (vec refresh-fx)}))

;; The editor closes at once, and the request follows.
(defevent-fx ::close-text
  [{:keys [db]} task-uuid field change-fx]
  {:db (-> db
           (lock/released (task-ident task-uuid) field)
           (assoc-in [::texts [task-uuid field] :change-fx] change-fx))
   :debounce/stop {:id (text-draft-id task-uuid field)}
   :dispatch [::send-text task-uuid field]})

;; A form going away closes whichever of its typed fields are open, and gives
;; up on any still being opened.
(defevent-fx ::close-texts
  [{:keys [db]} task-uuid change-fx]
  {:db (update db ::opening disj [task-uuid :task/title] [task-uuid :task/description])
   :fx (vec (for [field [:task/title :task/description]
                  :when (lock/held? db (task-ident task-uuid) field)]
              [:dispatch [::close-text task-uuid field change-fx]]))})

;; The page came back into view: each typed field it holds checks that its
;; lock is still its own. In this page's name, even lapsed, the lock is
;; renewed: nobody has written since. Moved elsewhere meanwhile — to another
;; tab of the user's, say — the field closes as a refused save closes it: with
;; the lock panel while it is held there, and read again once it was let go,
;; since the text may have changed.
(defevent-fx ::recheck-texts
  [{:keys [db]} task-uuid change-fx]
  (let [ident (task-ident task-uuid)]
    {:fx (vec (for [field [:task/title :task/description]
                    :when (lock/held? db ident field)]
                [:dispatch [::recheck-text task-uuid field change-fx]]))}))

(defevent-fx ::recheck-text
  [{:keys [db]} task-uuid field change-fx]
  {:db (assoc-in db [::texts [task-uuid field] :change-fx] change-fx)
   :resolve {:query [(lock/renew-form (task-ident task-uuid) field)]
             :on-error-fx [[:dispatch [::recheck-failed task-uuid field '?body]]]}})

;; Only a refusal closes the field; a request that failed otherwise says
;; nothing about the lock.
(defevent-fx ::recheck-failed
  [_ task-uuid field body]
  (if (lock/->refusal body)
    {:dispatch [::text-failed task-uuid field body]}
    {}))

(defn- text-query
  "The request for a typed field: its `value`, when there is text to save, and
   with `close?` the release of its lock after it."
  [task-uuid field value close?]
  (cond-> []
    (some? value)
    (conj {(list 'task/update {:task/uuid task-uuid
                               field value
                               :lock/client-uuid lock/client-uuid})
           [:task/uuid]})

    close?
    (conj (lock/release-form (task-ident task-uuid) field))))

;; The request is a save while the field is open and a close once it has been
;; closed, whenever it was asked for: one that waited — behind a request in
;; flight, or for another try — follows the field as it is by then, so a close
;; never lets go of a field that was opened again. While a request is in
;; flight, the next one waits for it.
(defevent-fx ::send-text
  [{:keys [db]} task-uuid field]
  (let [text-key [task-uuid field]
        step (if (lock/held? db (task-ident task-uuid) field) :save :close)
        value (text-to-send db task-uuid field)
        final-text (or value (get-in db [::texts text-key :sent]))]
    (cond
      (get-in db [::texts text-key :sending?])
      {:db (assoc-in db [::texts text-key :waiting?] true)}

      (and (= :save step) (nil? value))
      {:db (typing db task-uuid field false)}

      :else
      {:db (cond-> (-> db
                       (update-in [::texts text-key] assoc :sending? true :waiting? false)
                       (typing task-uuid field false))
             (some? value) (-> (patch-task task-uuid #(assoc % field value))
                               (write-started task-uuid)
                               (assoc-in [::writes task-uuid :typed?] true)))
       ;; The board shows the form, not the card, while the field is open, and
       ;; re-inits all its rows on a patch; it takes the text once, at the
       ;; close: what goes with it, or else what was saved before it.
       :fx (cond-> []
             (and (= :close step) (some? final-text))
             (conj [:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid {field final-text}]]))
       :resolve {:query (text-query task-uuid field value (= :close step))
                 :on-success-fx [[:dispatch [::text-sent task-uuid field step value]]]
                 :on-error-fx [[:dispatch [::text-failed task-uuid field '?body value]]]}})))

;; Other tabs hear of the text once it's closed, not at every pause. A field
;; opened again while its close was on its way keeps what it holds, and asks
;; for the lock that close let go.
(defevent-fx ::text-sent
  [{:keys [db]} task-uuid field step value]
  (let [text-key [task-uuid field]
        {:keys [waiting? change-fx]} (get-in db [::texts text-key])
        reopened? (and (= :close step)
                       (lock/held? db (task-ident task-uuid) field))
        db (cond-> (update-in db [::texts text-key] assoc :sending? false :waiting? false :failures 0)
             (some? value) (assoc-in [::texts text-key :sent] value))
        closed-fx (cond-> (into [tasks-changed-fx] change-fx)
                    reopened? (conj [:dispatch [::recheck-text task-uuid field change-fx]]))
        landed-fx (cond-> []
                    (some? value) (conj (write-ended-fx task-uuid true)))]
    (cond
      ;; Typed into, or closed, while the request was on its way. What waited
      ;; starts before this write counts as ended, so a re-read a Refresh left
      ;; waiting ([[::sync-now]]) waits for it too.
      waiting?
      {:db db
       :fx (cond-> (into [[:dispatch [::send-text task-uuid field]]] landed-fx)
             (= :close step) (into closed-fx))}

      (= :close step)
      {:db (cond-> db
             (not reopened?) (forget-text task-uuid field))
       :fx (into landed-fx closed-fx)}

      :else
      {:db db
       :fx landed-fx})))

(def ^:private field-nouns
  {:task/title "title"
   :task/description "description"})

(def ^:private text-retry-ms 5000)

(def ^:private max-text-retries 10)

(defn- failed-send-fx
  "What follows a typed field's save or close failing for the `failures`-th
   time in a row, short of a refusal: the request that waited behind it goes
   now, and otherwise it is tried again after [[text-retry-ms]], up to
   [[max-text-retries]] times. `toast-text`, given when text was at stake,
   shows at the first failure and when the retries run out, not at each one."
  [task-uuid field {:keys [failures waiting? toast-text]}]
  (cond-> []
    (and toast-text
         (or (= 1 failures) (= failures (inc max-text-retries))))
    (conj [:dispatch [:toast/error {:text toast-text}]])

    waiting?
    (conj [:dispatch [::send-text task-uuid field]])

    (and (not waiting?) (<= failures max-text-retries))
    (conj [:debounce/dispatch {:id (text-draft-id task-uuid field)
                               :event [::send-text task-uuid field]
                               :duration text-retry-ms}])))

;; Refused, the lock is gone: what was typed is dropped, and the host reads the
;; task again. Held by someone, the field's lock panel says who; gone with
;; nobody holding it, a toast says so if text typed here didn't land. Any other
;; failure, of a save or of a close, keeps the text, marked as not sent, and
;; tries again ([[failed-send-fx]]). `value` is the text the failed request
;; carried, if any.
(defevent-fx ::text-failed
  [{:keys [db]} task-uuid field body & [value]]
  (let [text-key [task-uuid field]
        {:keys [waiting? change-fx failures]} (get-in db [::texts text-key])
        ident (task-ident task-uuid)
        refusal (lock/->refusal body)
        text-dropped? (some? (or value (text-to-send db task-uuid field)))
        ended-fx (cond-> []
                   (some? value) (conj (write-ended-fx task-uuid false)))]
    (if refusal
      {:db (-> (if (:lost? refusal)
                 (lock/released db ident field)
                 (lock/refused db ident field refusal))
               (forget-text task-uuid field))
       :debounce/stop {:id (text-draft-id task-uuid field)}
       :fx (cond-> (into ended-fx change-fx)
             (and (:lost? refusal) text-dropped?)
             (conj [:dispatch [:toast/error {:text (str "Your last changes to the "
                                                        (field-nouns field)
                                                        " weren't saved.")}]]))}
      (let [failures (inc (or failures 0))]
        {:db (cond-> (update-in db [::texts text-key] assoc :sending? false :waiting? false :failures failures)
               (some? value) (typing task-uuid field true))
         ;; As in [[::text-sent]], what waited starts before this write ends.
         :fx (into (failed-send-fx task-uuid field {:failures failures
                                                    :waiting? waiting?
                                                    :toast-text (when (some? value)
                                                                  (error-text body))})
                   ended-fx)}))))

(defn- text-closed
  "`db` with a typed field closed as the page goes away: what was left to send
   counts as saved, since nothing will hear otherwise."
  [db [task-uuid field]]
  (let [value (text-to-send db task-uuid field)]
    (-> (cond-> db
          (some? value) (patch-task task-uuid #(assoc % field value)))
        (lock/released (task-ident task-uuid) field)
        (forget-text task-uuid field))))

;; The page is going away. Every typed field whose close hasn't landed — still
;; open, or closed with its request on the way, which the browser may drop —
;; closes now: the text the platform hasn't confirmed and the release go in
;; one request, sent so that it outlives the page, whatever is in flight.
(defevent-fx ::page-hidden
  [{:keys [db]} _]
  (let [text-keys (keys (::texts db))]
    (if (seq text-keys)
      {:db (reduce text-closed db text-keys)
       :resolve {:query (into []
                              (mapcat (fn [[task-uuid field]]
                                        (text-query task-uuid field (text-to-send db task-uuid field) true)))
                              text-keys)
                 :keepalive true}}
      {})))

(defn init!
  "Each surface calls this at boot, so what is typed still reaches the platform
   when the page goes away."
  []
  (js/window.addEventListener "pagehide" #(>e! [::page-hidden])))

(defevent ::clear [db _]
  (dissoc db ::tasks ::load-result ::highlighted-uuid))

(defsub ::list [db]
  (get db ::tasks []))

(defsub ::loaded? [db]
  (contains? db ::tasks))

(defsub ::highlighted? [db task-uuid]
  (= task-uuid (::highlighted-uuid db)))

;; Today's completions carried in the loaded slice (Home only — the page loads
;; open tasks only), oldest-first for the progress timeline. Only the tasks
;; assigned to the user: the slice also holds ones they own but assigned to
;; someone else.
(defsub ::completed-today
  [current-user <- [:auth/identity]
   tasks <- [::list]]
  (->> tasks
       ;; Only actual completions — an open task's `(js/Date. nil)` → NaN would
       ;; poison the Progress timeline's window math.
       (filter :task/completed-at)
       (filter #(assignee-me? current-user (:task/assigned-to %)))
       (sort-by #(.getTime (js/Date. (:task/completed-at %))))))

;; Open tasks in the loaded slice whose deadline lands on today, earliest first.
;; Home's Progress card shows these as pills beside the day's all-day calendar
;; events. Scope follows the slice: `:user/current-tasks` is owned-or-assigned,
;; so a task only shared with the user doesn't appear — the same scope every
;; other card on the page uses.
(defsub ::deadlines-today
  [tasks <- [::list]]
  (->> tasks
       (filter deadline/due-today?)
       (sort-by (juxt #(.getTime (:task/deadline-at %)) :task/title))))

(defn- sort-bucket
  "Newest first by creation time."
  [tasks]
  (sort-by #(.getTime (js/Date. (:task/created-at %))) > tasks))

(defn bucket-tasks
  "Group tasks into :none / :now / :next / :later by urgency, each newest-first.
   Tasks with no urgency land in :none (see `task-bucket`)."
  [tasks]
  (let [grouped (group-by task-bucket tasks)]
    {:none  (sort-bucket (:none grouped))
     :now   (sort-bucket (:now grouped))
     :next  (sort-bucket (:next grouped))
     :later (sort-bucket (:later grouped))}))

(defsub ::buckets
  [tasks <- [::list]]
  (bucket-tasks tasks))

(def bucket-tabs
  "Ordered tabs over `::buckets`: bucket id, display label and, where the label
   wants explaining, a `:tooltip`. The tasks nobody has placed yet lead, the
   way the ＋ pill's menu lists them, above the lanes they get placed into."
  [{:id :none
    :label (urgency/label urgency/none)
    :tooltip "This is the place where things land ready to be sorted"}
   {:id :now :label (urgency/label :task.urgency/now)}
   {:id :next :label (urgency/label :task.urgency/next)}
   {:id :later :label (urgency/label :task.urgency/later)}])

(def default-open-bucket-ids
  "The tabs a tasks card shows open at first: the lanes. The tasks nobody has
   placed yet start folded."
  (disj (into #{} (map :id) bucket-tabs) :none))

;; ---------------------------------------------------------------------------
;; Board facets — option list + SMART counts for the kanban assignee filter
;; sidebar (`hai.pathom.tasks/user-task-facets`, [HAI-006-01]). Each dimension's
;; counts respect the board's live search + the OTHER dimensions' filters, sent
;; as query params. Mirrors `hai.notes.data`'s facets section. Loaded on the
;; sidebar's mount ([HAI-006-03]), after task mutations, and after every filter
;; or (debounced) search change ([HAI-006-04]).
;; ---------------------------------------------------------------------------

(def ^:private facets-eql
  [:facet/total
   :facet/unassigned-count
   :facet/untagged-count
   {:facet/tags [:tag/name :facet/count]}
   {:facet/assignees [:user/uuid :user/full-name :facet/count]}])

(defn- live-filters
  "The board's active filter values, read from the filters SUB-form's fields —
   not the root form's `:table/filters` map. A sub-field write lands in the
   sub-form's field state synchronously but reaches the root map only through a
   chain of queued events, so a facet-click handler dispatching [[::load-facets]]
   right after `set-value!` would read a stale root. Degrades to `{}` while the
   board declares no filter columns ([HAI-006-04]) and the sub-form is unmounted:
   `get-in` returns nil and `keep` yields nothing rather than throwing."
  [db]
  (into {}
        (keep (fn [[attr {current-value :form.field/current-value}]]
                (when (some? current-value)
                  [attr current-value])))
        (get-in db [:forms [board-collection-id :table/filters] :form/fields])))

;; Self-guarded on the board being mounted, unlike notes' unconditional wall
;; reload: task mutations also fire from Home, the detail page, and the mobile
;; add-sheet, where no board (and no facets sidebar) is rendered — so this is
;; the facets analog of the board's own `:table/resolve-if-mounted`. On the
;; sidebar mount and on filter/search changes the board is always present, so
;; the guard is a no-op there.
(defevent-fx ::load-facets
  [{:keys [db]} _]
  (when (get-in db [:collections board-collection-id])
    (let [active (live-filters db)
          search (get-in db [:forms board-collection-id :form/fields
                             :table/search :form.field/current-value])
          params (cond-> {}
                   (seq active) (assoc :collection.filters/selected active)
                   (some-> search str/trim not-empty) (assoc :search search))]
      {:resolve {:query        [(if (seq params)
                                  (list {:user/task-facets facets-eql} params)
                                  {:user/task-facets facets-eql})]
                 :transform-fn :user/task-facets
                 :store-in     [::facets]}})))

;; Search keystrokes funnel through this so the counts re-query once typing
;; pauses — the same cadence as the board's own debounced resolve ([HAI-006-04]).
(defevent-fx ::load-facets-debounced
  [_ _]
  {:debounce/dispatch {:id ::load-facets
                       :event [::load-facets]
                       :duration 500}})

(defsub ::facets [db]
  (get db ::facets))
